"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const deepmerge_1 = __importDefault(require("deepmerge"));
const registerFinalBeacon_1 = __importDefault(require("./beacon/registerFinalBeacon"));
const triggerBeacon_1 = __importDefault(require("./beacon/triggerBeacon"));
const events_1 = require("./constants/events");
const registerEvents_1 = require("./events/registerEvents");
const registerTrackedEvents_1 = __importDefault(require("./events/registerTrackedEvents"));
const initConfig_1 = __importDefault(require("./init/initConfig"));
const initOnLoadBeacon_1 = __importDefault(require("./init/initOnLoadBeacon"));
const pkgInfo_1 = require("./pkgInfo");
const profileEvent_1 = __importStar(require("./profile/profileEvent"));
const profileEventInterval_1 = require("./profile/profileEventInterval");
const NavigationTimings_1 = __importDefault(require("./timings/NavigationTimings"));
const ResourceTimings_1 = __importDefault(require("./timings/ResourceTimings"));
const Debug_1 = __importDefault(require("./utils/Debug"));
const setAppInfo_1 = __importDefault(require("./utils/setAppInfo"));
const WebVitalsMetrics_1 = __importDefault(require("./web-vitals/WebVitalsMetrics"));
/**
 * ClientProfiler is the main class for profiling events in the browser. It
 * automatically profiles Web Vitals events and provides a public API for
 * profiling custom events.
 *
 * @todo Handle top-level typeof window === `undefined`, e.g., in node
 *   environment (PERF-802)
 */
class ClientProfiler {
    /** Create a new ClientProfiler instance with an initial configuration */
    constructor(config) {
        /** Queue of completed event objects to beacon @private */
        this.eventEndQueue = [];
        /** Map of registered events @private */
        this.eventRegistry = {};
        /** Queue of started event names @private */
        this.eventStartQueue = [];
        // Register ----------------------------------------------------------------
        /**
         * Register a custom RUM event. If the event has already been registered,
         * the existing `thresholds` and `opts` will be replaced with the new
         * values.
         *
         * _**Note:** Unregistered events will be ignored when beaconing!_
         *
         * @example
         *   // Register a static event
         *   registerEvent('static-event', [100, 200]);
         *
         *   // Register a dynamic event
         *   registerEvent('dynamic-event', [300, 400], { isDynamic: true });
         *
         *   // Register a dynamic event with custom graphite name
         *   registerEvent('dynamic-event', [500, 600], {
         *       graphiteName: 'dynamic-event-custom-graphite-name',
         *       isDynamic: true,
         *   });
         */
        this.registerEvent = (eventName, thresholds, opts) => (0, registerEvents_1.registerEvent)(this, eventName, Object.assign({ thresholds }, opts));
        /**
         * Convenience method for registering multiple custom RUM events at once.
         * See `registerEvent()` for details.
         *
         * @example
         *   registerEvents({
         *       'static-event': { thresholds: [100, 200] },
         *       'dynamic-event': { isDynamic: true, thresholds: [300, 400] },
         *       'dynamic-event': {
         *           graphiteName: 'dynamic-event-graphite-name',
         *           isDynamic: true,
         *           thresholds: [500, 600],
         *       },
         *   });
         *
         *   // ... would be equivalent to:
         *
         *   registerEvent('static-event', [100, 200]);
         *   registerEvent('dynamic-event', [300, 400], { isDynamic: true });
         *   registerEvent('dynamic-event', [500, 600], {
         *       graphiteName: 'dynamic-event-graphite-name',
         *       isDynamic: true,
         *   });
         */
        this.registerEvents = (events) => (0, registerEvents_1.registerEvents)(this, events);
        // Beacon ------------------------------------------------------------------
        /**
         * Beacon all completed events queued up since last call. See
         * `triggerBeacon()` for details.
         *
         * Returns `true` if successful, `false` otherwise.
         *
         * _**Note:** You shouldn't have to call this manually! Beacons are sent
         * automatically at various times during the page lifecycle. See
         * ../README.md#beacons--payloads for details._
         */
        this.beacon = (opts) => (0, triggerBeacon_1.default)(this, opts);
        // Profile -----------------------------------------------------------------
        /**
         * Profile a static event `eventName`: Measure elapsed time since the
         * document's [timeOrigin][] and queue the result to be sent with the next
         * beacon.
         *
         * Returns `true` if successful, `false` otherwise.
         *
         * _**Note:** Static events can only be profiled once per session.
         * Subsequent attempts to profile events of the same name will be ignored._
         *
         * [timeOrigin]: https://developer.mozilla.org/en-US/docs/Web/API/Performance/timeOrigin
         */
        this.profile = (eventName) => (0, profileEvent_1.default)(this, eventName);
        /**
         * Begin profiling a dynamic event interval `eventName`: Mark the start time
         * so the duration can be measured later with `profileIntervalEnd()`.
         *
         * Returns `true` if successful, `false` otherwise.
         *
         * _**Note:** Dynamic events can be profiled multiple times per session, but
         * only one interval can be active at once. Attempts to begin profiling an
         * interval before the previous one has ended will be ignored._
         */
        this.profileIntervalBegin = (eventName) => (0, profileEventInterval_1.profileIntervalBegin)(this, eventName);
        /**
         * End profiling a dynamic event interval `eventName` started previously
         * with `profileIntervalBegin()`: Mark the end time of the event, measure
         * the interval duration, and trigger a beacon immediately.
         *
         * A custom `startEventName` can be provided to use a different event's
         * start time to measure the interval. This can be useful for when multiple
         * dynamic events share the same start time, e.g., relative to when a
         * primary component has loaded.
         *
         * Returns `true` if successful, `false` otherwise.
         */
        this.profileIntervalEnd = (eventName, startEventName) => (0, profileEventInterval_1.profileIntervalEnd)(this, eventName, { startEventName });
        /**
         * Abort profiling a dynamic event `eventName` interval started with
         * `profileIntervalBegin()`: Set the event's interval duration to -1 and
         * trigger a beacon. Useful for indicating a failure, e.g., the profile
         * target fails to load.
         *
         * Returns `true` if successful, `false` otherwise.
         */
        this.profileIntervalFail = (eventName) => (0, profileEventInterval_1.profileIntervalEnd)(this, eventName, { isFailure: true });
        // Deprecated --------------------------------------------------------------
        /**
         * Create a new event with an arbitrary value and beacon immediately.
         *
         * @param eventName - Name of the event to profile
         * @param value - Value to profile
         * @deprecated - This functionality will be removed in a future release.
         */
        this.profileArbitraryEvent = (eventName, value) => (0, profileEvent_1.profileArbitraryEvent)(this, eventName, value);
        // Expose app version info client-side via the `appInfo` convention to
        // assist with testing and debugging. Note: these values are imported
        // from `../pkgInfo` which is generated at build time.
        (0, setAppInfo_1.default)(pkgInfo_1.packageName, pkgInfo_1.gitShortHash);
        // Init debug mode
        this.debug = new Debug_1.default();
        // Init Client Profiler configuration
        this.internalConfig = (0, initConfig_1.default)(config);
        // Register events from the `trackedEventDetails` config option
        // (Deprecated. See PERF-836.)
        (0, registerTrackedEvents_1.default)(this);
        // Init browser timings, including Web Vitals
        this.navigationTimings = new NavigationTimings_1.default();
        this.resourceTimings = new ResourceTimings_1.default();
        this.webVitalsMetrics = new WebVitalsMetrics_1.default(this.debug);
        // Init load event profiling and beaconing
        (0, initOnLoadBeacon_1.default)(this);
        // Register final beacon to fire when the user leaves the page
        (0, registerFinalBeacon_1.default)(this.beacon);
        // Immediately consider the `clientProfilerLoaded` event complete since
        // the bundle has loaded and initialized successfully
        this.profile(events_1.EVENT_NAMES.CLIENT_PROFILER_LOADED);
        // Fire initial beacon which should only contain INs for all registered
        // static events, and initial OUT events (TTFB and clientProfilerLoaded)
        this.beacon();
    }
    // Configure ---------------------------------------------------------------
    /** Merge a new appConfig into existing appConfig */
    set appConfig(appConfig) {
        this.internalConfig = (0, deepmerge_1.default)(this.internalConfig, appConfig);
    }
    /** Return the current appConfig */
    get appConfig() {
        return this.internalConfig;
    }
}
exports.default = ClientProfiler;
